import { useMemo } from "react";
import { SEO } from "@/components/SEO";
import { RecentArticlesSection } from "@/components/home/RecentArticlesSection";
import { MostReadSection, MostReadSkeleton } from "@/components/home/MostReadSection";
import { CategorySection } from "@/components/home/CategorySection";
import { useArticlesData } from "@/hooks/useArticlesData";
import { useCategories } from "@/hooks/useCategories";
import { ArticleGridSkeleton } from "@/components/skeletons/ArticleCardSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { PageError } from "@/components/PageState";

const Index = () => {
  const { articles, loading, error, getByCategory, refresh } =
    useArticlesData();
  const { categories, loading: catsLoading } = useCategories();

  const isLoading = loading || catsLoading;

  const categoriesWithArticles = useMemo(() => {
    return categories
      .map((cat) => ({
        category: cat,
        articles: getByCategory(cat.slug),
      }))
      .filter((item) => item.articles.length > 0);
  }, [categories, getByCategory]);

  return (
    <>
      <SEO />
      {error ? (
        <div className="container py-8">
          <PageError
            variant="server"
            title="We couldn't load the latest articles"
            message={error.message || "Please try again in a moment."}
            onRetry={refresh}
          />
        </div>
      ) : (
        <>
          <div className="container py-8">
            {isLoading ? (
              <ArticleGridSkeleton
                count={8}
                cols="sm:grid-cols-2 lg:grid-cols-4"
              />
            ) : (
              <RecentArticlesSection
                articles={articles}
                categories={categories}
                getByCategory={getByCategory}
              />
            )}
          </div>

          {isLoading ? (
            <MostReadSkeleton />
          ) : (
            <MostReadSection articles={articles} />
          )}

          <div className="container py-8 space-y-4">
            {isLoading ? (
              <>
                <Skeleton className="h-6 w-48 mb-4" />
                <ArticleGridSkeleton
                  count={6}
                  cols="sm:grid-cols-2 lg:grid-cols-3"
                />
                <Skeleton className="h-6 w-48 mb-4 mt-8" />
                <ArticleGridSkeleton
                  count={6}
                  cols="sm:grid-cols-2 lg:grid-cols-3"
                />
              </>
            ) : (
              categoriesWithArticles.map(({ category, articles: catArticles }) => (
                <CategorySection
                  key={category.id}
                  category={category}
                  articles={catArticles}
                  maxArticles={6}
                />
              ))
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Index;
